import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix broken default marker icons in webpack builds
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Inner component that flies to new coordinates when they change
const FlyToCoords = ({ lat, lng }) => {
  const map = useMap();
  useEffect(() => {
    map.flyTo([lat, lng], 15);
  }, [lat, lng, map]);
  return null;
};

const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

const resolveCoordinates = async (address) => {
  // Parse "street, number, district, city" format built from DB fields
  const parts = address.split(',').map(s => s.trim()).filter(Boolean);
  const city = parts[parts.length - 1] || '';
  const street = parts.slice(0, 2).join(' ').trim(); // street + building_number

  const tryStructured = async (streetQ, cityQ) => {
    if (!streetQ || !cityQ) return null;
    const response = await fetch(
      `${NOMINATIM_URL}?` + new URLSearchParams({
        street: streetQ,
        city: cityQ,
        country: 'Poland',
        format: 'json',
        limit: 1,
        'accept-language': 'pl',
      })
    );
    const data = await response.json();
    return data?.[0] ?? null;
  };

  const tryFree = async (q) => {
    const response = await fetch(
      `${NOMINATIM_URL}?` + new URLSearchParams({
        q,
        format: 'json',
        limit: 1,
        countrycodes: 'pl',
        'accept-language': 'pl',
      })
    );
    const data = await response.json();
    return data?.[0] ?? null;
  };

  // 1. Structured search: street + city (most accurate)
  let result = await tryStructured(street, city);
  if (result) return { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };

  // 2. Free-form full address
  result = await tryFree(address);
  if (result) return { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };

  // 3. Street name only + city (drop building number)
  const streetNameOnly = parts[0] || '';
  result = await tryStructured(streetNameOnly, city);
  if (result) return { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };

  // 4. City only fallback
  result = await tryFree(city || 'Warszawa');
  if (result) return { lat: parseFloat(result.lat), lng: parseFloat(result.lon) };

  return { lat: 52.2297, lng: 21.0122 };
};

const MapComponent = ({ address, title = "Lokalizacja", lat = null, lng = null }) => {
  const [coordinates, setCoordinates] = useState(
    lat && lng ? { lat, lng } : null
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Jeśli mamy gotowe współrzędne z bazy — nie wywołuj Nominatim
    if (lat && lng) {
      setCoordinates({ lat, lng });
      return;
    }
    if (!address) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    resolveCoordinates(address)
      .then((coords) => { if (!cancelled) setCoordinates(coords); })
      .catch(() => { if (!cancelled) setError('Błąd podczas ładowania mapy'); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [address, lat, lng]);

  if (!address) return null;

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: '600' }}>
        {title}
      </Typography>
      <Paper
        elevation={1}
        sx={{
          overflow: 'hidden',
          borderRadius: 2,
          height: 300,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {loading && (
          <Box sx={{ textAlign: 'center', position: 'absolute', zIndex: 1000 }}>
            <CircularProgress size={40} sx={{ color: '#D2691E' }} />
            <Typography variant="body2" sx={{ mt: 1, color: '#666' }}>
              Ładowanie mapy...
            </Typography>
          </Box>
        )}

        {error && !loading && (
          <Typography variant="body2" sx={{ color: '#666', textAlign: 'center' }}>
            {error}
          </Typography>
        )}

        {coordinates && (
          <MapContainer
            center={[coordinates.lat, coordinates.lng]}
            zoom={15}
            style={{ width: '100%', height: '100%' }}
            scrollWheelZoom={false}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <FlyToCoords lat={coordinates.lat} lng={coordinates.lng} />
            <Marker position={[coordinates.lat, coordinates.lng]}>
              <Popup>{address}</Popup>
            </Marker>
          </MapContainer>
        )}
      </Paper>
      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 1, color: '#666', textAlign: 'center' }}
      >
        📍 {address}
      </Typography>
    </Box>
  );
};

export default MapComponent;